import * as Sentry from '@sentry/vue'
// Imported statically: a lazy chunk would start observing after hydration and miss the interactions made while the
// page loads
import { onINP, type INPAttribution, type INPMetricWithAttribution } from 'web-vitals/attribution'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

import { INP_TRANSACTION_OP, TAG_VALUE_MAX_LENGTH } from '@layers/web/constants/sentry.ts'
import type { ShownPage } from '@layers/web/types/sentry.ts'

const toShownPage = (route: RouteLocationNormalizedLoaded, shownAt: number): ShownPage => ({
  shownAt,
  routeName: route.name?.toString() ?? route.path,
})

const getInpTags = ({ rating, attribution }: INPMetricWithAttribution): Record<string, string> => {
  const { entry, subpart } = attribution.longestScript ?? {}
  const tags = {
    'inp.target': attribution.interactionTarget,
    'inp.interaction_type': attribution.interactionType,
    'inp.load_state': attribution.loadState,
    'inp.rating': rating,
    'inp.script.subpart': subpart,
    'inp.script.invoker': entry?.invoker,
    'inp.script.invoker_type': entry?.invokerType,
    'inp.script.source_url': entry?.sourceURL,
    'inp.script.function': entry?.sourceFunctionName,
    'inp.script.char_position': entry?.sourceCharPosition?.toString(),
  }

  return Object.fromEntries(
    Object.entries(tags)
      .filter((tag): tag is [string, string] => Boolean(tag[1]))
      .map(([key, value]) => [key, value.slice(0, TAG_VALUE_MAX_LENGTH)])
  )
}

const setInpMeasurements = (value: number, attribution: INPAttribution) => {
  const durations = {
    'inp': value,
    'inp.input_delay': attribution.inputDelay,
    'inp.processing_duration': attribution.processingDuration,
    'inp.presentation_delay': attribution.presentationDelay,
    'inp.longest_script': attribution.longestScript?.intersectingDuration,
    'inp.total_script': attribution.totalScriptDuration,
    'inp.total_style_and_layout': attribution.totalStyleAndLayoutDuration,
    'inp.total_paint': attribution.totalPaintDuration,
    'inp.total_unattributed': attribution.totalUnattributedDuration,
  }

  Object.entries(durations)
    .filter((duration): duration is [string, number] => duration[1] !== undefined)
    .forEach(([name, duration]) => Sentry.setMeasurement(name, duration, 'millisecond'))
}

const reportInpToSentry = (metric: INPMetricWithAttribution, interactionTime: number, routeName: string) => {
  const startTime = (performance.timeOrigin + interactionTime) / 1000

  Sentry.withScope((scope) => {
    scope.setTags(getInpTags(metric))
    Sentry.startSpanManual({
      name: routeName,
      op: INP_TRANSACTION_OP,
      forceTransaction: true,
      startTime,
    }, (span) => {
      setInpMeasurements(metric.value, metric.attribution)
      span?.end(startTime + metric.value / 1000)
    })
  })
}

/**
 * Sends the INP of each page visit to Sentry with the element, the page it was on, the load state and the longest
 * script, so the slowest interactions can be found per page and element
 */
export default defineNuxtPlugin((nuxtApp) => {
  if (!Sentry.getClient()) {
    return
  }

  const router = useRouter()
  const shownPages = [toShownPage(router.currentRoute.value, 0)]

  // The previous page stays on screen until the next one has loaded, so a page counts from page:finish
  nuxtApp.hook('page:finish', () => {
    shownPages.push(toShownPage(router.currentRoute.value, performance.now()))
  })

  onINP((metric) => {
    // No interaction time means the INP was below the reporting threshold and only the input delay is known
    const interactionTime = metric.attribution.interactionTime ?? performance.now() - metric.value
    const shownPage = shownPages.findLast(page => page.shownAt <= interactionTime) ?? shownPages[0]

    reportInpToSentry(metric, interactionTime, shownPage.routeName)
  })
})
